// src/pages/CheckinAnswer.js
//
// One question per screen. Save on explicit Next / Skip / Done.
// Detects which user is viewing and loads/saves their own responses.
//
// FIX (2026-05-07):
// - Removed onBlur save (was firing twice with button onClick on mobile,
//   causing duplicate inserts and unreliable behavior on iOS Safari).
// - Save now happens only on explicit Next/Skip/Done.
// - Errors are caught and surfaced to the user instead of failing silently.
// - saveCurrentDraft re-reads existing row from DB inside the function to avoid
//   stale React state causing INSERT vs UPDATE confusion.
// - finishAnswering re-counts from DB after save to determine status.
//
import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { theme } from '../lib/theme'

const c = theme.colors
const f = theme.fonts

export default function CheckinAnswer() {
  const { id: conversationId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [conversation, setConversation] = useState(null)
  const [questions, setQuestions] = useState([])
  const [responses, setResponses] = useState({})
  const [currentIdx, setCurrentIdx] = useState(0)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const textareaRef = useRef(null)

  useEffect(() => { fetchAll() }, [conversationId])

  // Load draft for current question whenever index/questions change
  useEffect(() => {
    if (questions.length === 0) return
    const cq = questions[currentIdx]
    if (!cq) return
    const existing = responses[cq.id]
    setDraft(existing?.answer_text || '')
    setErrorMsg('')
    setTimeout(() => textareaRef.current?.focus(), 50)
  }, [currentIdx, questions])

  async function fetchAll() {
    setLoading(true)
    setErrorMsg('')
    try {
      const { data: { user: u } } = await supabase.auth.getUser()
      if (!u) { setLoading(false); return }
      setUser(u)

      const { data: convo, error: convoErr } = await supabase
        .from('money_conversations')
        .select('*')
        .eq('id', conversationId)
        .maybeSingle()
      if (convoErr) throw convoErr
      if (!convo) { navigate('/checkins'); return }
      setConversation(convo)

      const { data: questionRows, error: qErr } = await supabase
        .from('conversation_questions')
        .select('id, custom_text, sort_order, question_library_id, question_library(text)')
        .eq('conversation_id', conversationId)
        .order('sort_order', { ascending: true })
      if (qErr) throw qErr

      const flatQuestions = (questionRows || []).map(q => ({
        id: q.id,
        text: q.custom_text || q.question_library?.text || '(no text)',
        sort_order: q.sort_order,
      }))
      setQuestions(flatQuestions)

      const cqIds = flatQuestions.map(q => q.id)
      if (cqIds.length > 0) {
        const { data: resps, error: rErr } = await supabase
          .from('conversation_responses')
          .select('*')
          .eq('user_id', u.id)
          .in('conversation_question_id', cqIds)
        if (rErr) throw rErr
        const respMap = {}
        for (const r of (resps || [])) {
          respMap[r.conversation_question_id] = r
        }
        setResponses(respMap)

        // Resume on first un-answered question if user already started
        const firstUnansweredIdx = flatQuestions.findIndex(q => {
          const r = respMap[q.id]
          return !r || (!r.answer_text && !r.skipped)
        })
        if (firstUnansweredIdx > 0) setCurrentIdx(firstUnansweredIdx)
      }
    } catch (err) {
      console.error('CheckinAnswer fetch failed:', err)
      setErrorMsg('Could not load this conversation. Try refreshing.')
    } finally {
      setLoading(false)
    }
  }

  // Save current draft. Returns true on success, false on failure.
  // Re-reads existing row from DB to avoid stale state causing duplicate inserts.
  const saveCurrentDraft = async () => {
    if (!user) return false
    const cq = questions[currentIdx]
    if (!cq) return false

    const text = draft.trim()
    if (!text) return true // nothing to save, treat as success

    setSaving(true)
    setErrorMsg('')

    try {
      // Re-read the current user's existing row for this question, fresh from DB
      const { data: existingRow, error: lookupErr } = await supabase
        .from('conversation_responses')
        .select('*')
        .eq('conversation_question_id', cq.id)
        .eq('user_id', user.id)
        .maybeSingle()
      if (lookupErr) throw lookupErr

      if (existingRow) {
        // UPDATE
        const { data: updated, error: updErr } = await supabase
          .from('conversation_responses')
          .update({
            answer_text: text,
            skipped: false,
            answered_at: new Date().toISOString(),
          })
          .eq('id', existingRow.id)
          .select()
          .single()
        if (updErr) throw updErr
        setResponses(prev => ({ ...prev, [cq.id]: updated }))
      } else {
        // INSERT
        const { data: inserted, error: insErr } = await supabase
          .from('conversation_responses')
          .insert({
            conversation_question_id: cq.id,
            user_id: user.id,
            answer_text: text,
            skipped: false,
          })
          .select()
          .single()
        if (insErr) throw insErr
        setResponses(prev => ({ ...prev, [cq.id]: inserted }))
      }
      setSaving(false)
      return true
    } catch (err) {
      console.error('Save failed:', err)
      setErrorMsg(`Could not save your answer. ${err.message || 'Please try again.'}`)
      setSaving(false)
      return false
    }
  }

  const skipCurrent = async () => {
    if (!user) return
    const cq = questions[currentIdx]
    if (!cq) return

    setSaving(true)
    setErrorMsg('')

    try {
      const { data: existingRow } = await supabase
        .from('conversation_responses')
        .select('*')
        .eq('conversation_question_id', cq.id)
        .eq('user_id', user.id)
        .maybeSingle()

      if (existingRow) {
        const { data: updated, error } = await supabase
          .from('conversation_responses')
          .update({
            answer_text: null,
            skipped: true,
            answered_at: new Date().toISOString(),
          })
          .eq('id', existingRow.id)
          .select()
          .single()
        if (error) throw error
        setResponses(prev => ({ ...prev, [cq.id]: updated }))
      } else {
        const { data: inserted, error } = await supabase
          .from('conversation_responses')
          .insert({
            conversation_question_id: cq.id,
            user_id: user.id,
            skipped: true,
          })
          .select()
          .single()
        if (error) throw error
        setResponses(prev => ({ ...prev, [cq.id]: inserted }))
      }
      setSaving(false)
      // Move to next
      if (currentIdx < questions.length - 1) {
        setCurrentIdx(currentIdx + 1)
      }
    } catch (err) {
      console.error('Skip failed:', err)
      setErrorMsg(`Could not save skip. ${err.message || 'Please try again.'}`)
      setSaving(false)
    }
  }

  const goNext = async () => {
    const ok = await saveCurrentDraft()
    if (!ok) return // don't advance if save failed
    if (currentIdx < questions.length - 1) {
      setCurrentIdx(currentIdx + 1)
    }
  }

  const goPrev = async () => {
    const ok = await saveCurrentDraft()
    if (!ok) return
    if (currentIdx > 0) {
      setCurrentIdx(currentIdx - 1)
    }
  }

  const finishAnswering = async () => {
    setFinishing(true)
    const ok = await saveCurrentDraft()
    if (!ok) {
      setFinishing(false)
      return
    }

    try {
      // Re-fetch ALL responses (across both partners) for this conversation,
      // so we can decide if status should flip to ready_to_review.
      const cqIds = questions.map(q => q.id)
      const { data: allResps, error: respErr } = await supabase
        .from('conversation_responses')
        .select('user_id, conversation_question_id, answer_text, skipped')
        .in('conversation_question_id', cqIds)
      if (respErr) throw respErr

      // Count how many questions each user has actually answered or skipped
      const userResponseCount = {}
      for (const r of (allResps || [])) {
        if (r.answer_text || r.skipped) {
          userResponseCount[r.user_id] = (userResponseCount[r.user_id] || 0) + 1
        }
      }
      const usersWhoFinished = Object.entries(userResponseCount)
        .filter(([uid, count]) => count >= questions.length)
        .map(([uid]) => uid)

      const newStatus = usersWhoFinished.length >= 2 ? 'ready_to_review' : 'awaiting_partner'

      const { error: updErr } = await supabase
        .from('money_conversations')
        .update({ status: newStatus })
        .eq('id', conversationId)
      if (updErr) throw updErr

      setFinishing(false)
      navigate('/checkins')
    } catch (err) {
      console.error('Finish failed:', err)
      setErrorMsg(`Could not submit. ${err.message || 'Please try again.'}`)
      setFinishing(false)
    }
  }

  if (loading) {
    return <div style={styles.loading}>Loading…</div>
  }

  if (questions.length === 0) {
    return (
      <div style={styles.page}>
        <p style={styles.errorText}>This conversation has no questions yet.</p>
        <button onClick={() => navigate('/checkins')} style={styles.backBtn}>
          Back to Check-ins
        </button>
      </div>
    )
  }

  const totalQuestions = questions.length
  const current = questions[currentIdx]
  const progress = Math.round(((currentIdx + 1) / totalQuestions) * 100)
  const answeredCount = Object.values(responses).filter(r => r.answer_text || r.skipped).length
  const allAnswered = answeredCount >= totalQuestions
  const isLastQuestion = currentIdx === totalQuestions - 1

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <button onClick={() => navigate('/checkins')} style={styles.exitBtn}>
          ‹ Save & exit
        </button>
        <div style={styles.progressWrap}>
          <div style={styles.progressBarBg}>
            <div style={{ ...styles.progressBarFill, width: `${progress}%` }} />
          </div>
          <div style={styles.progressText}>
            Question {currentIdx + 1} of {totalQuestions}
          </div>
        </div>
      </header>

      {errorMsg && (
        <div style={styles.errorBanner}>
          <span>{errorMsg}</span>
        </div>
      )}

      <div style={styles.questionCard}>
        <div style={styles.questionText}>{current.text}</div>
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Take your time…"
          style={styles.textarea}
          rows={6}
        />
        {saving && (
          <div style={styles.savingDot}>Saving…</div>
        )}
      </div>

      <div style={styles.actionsRow}>
        <button
          onClick={goPrev}
          disabled={currentIdx === 0 || saving || finishing}
          style={{
            ...styles.secondaryBtn,
            opacity: (currentIdx === 0 || saving || finishing) ? 0.4 : 1,
            cursor: (currentIdx === 0 || saving || finishing) ? 'not-allowed' : 'pointer',
          }}
        >
          ‹ Previous
        </button>
        <button
          onClick={skipCurrent}
          disabled={saving || finishing}
          style={{
            ...styles.skipBtn,
            opacity: (saving || finishing) ? 0.4 : 1,
            cursor: (saving || finishing) ? 'not-allowed' : 'pointer',
          }}
        >
          Skip
        </button>
        {isLastQuestion ? (
          <button
            onClick={finishAnswering}
            disabled={finishing || saving}
            style={{
              ...styles.primaryBtn,
              opacity: (finishing || saving) ? 0.6 : 1,
              cursor: (finishing || saving) ? 'wait' : 'pointer',
            }}
          >
            {finishing ? 'Finishing…' : (saving ? 'Saving…' : 'Done, submit answers')}
          </button>
        ) : (
          <button
            onClick={goNext}
            disabled={saving || finishing}
            style={{
              ...styles.primaryBtn,
              opacity: (saving || finishing) ? 0.6 : 1,
              cursor: (saving || finishing) ? 'wait' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Next'} ›
          </button>
        )}
      </div>

      <div style={styles.bottomMeta}>
        <span>{answeredCount} of {totalQuestions} answered</span>
        {allAnswered && !isLastQuestion && (
          <span style={styles.allDoneHint}>You can submit when ready</span>
        )}
      </div>
    </div>
  )
}

const styles = {
  page: { padding: '24px', maxWidth: '740px', margin: '0 auto' },
  loading: { padding: '40px', textAlign: 'center', color: c.inkMuted },
  errorText: { color: c.inkMuted, fontSize: '14px', textAlign: 'center', padding: '40px' },

  header: { marginBottom: '24px' },
  exitBtn: {
    background: 'none', border: 'none',
    color: c.inkMuted, fontSize: '13px',
    cursor: 'pointer', padding: '4px 0',
    display: 'inline-flex', alignItems: 'center', gap: '2px',
    marginBottom: '14px',
    fontFamily: 'inherit',
  },
  progressWrap: { display: 'flex', flexDirection: 'column', gap: '6px' },
  progressBarBg: {
    width: '100%', height: '6px',
    background: c.line, borderRadius: '3px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%', background: c.green,
    borderRadius: '3px',
    transition: 'width 220ms ease-out',
  },
  progressText: {
    fontSize: '12px', color: c.inkMuted, fontWeight: 500,
    fontFamily: f.body || 'inherit',
  },

  errorBanner: {
    background: '#FEE2E2', border: '1px solid #FCA5A5',
    color: '#991B1B', borderRadius: '10px',
    padding: '10px 14px', marginBottom: '14px',
    fontSize: '13px', fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: '8px',
    lineHeight: 1.4,
  },

  questionCard: {
    background: c.white, borderRadius: '14px',
    border: `1px solid ${c.line}`,
    padding: '28px',
    marginBottom: '20px',
    minHeight: '300px',
  },
  questionText: {
    fontFamily: f.display, fontSize: '22px', fontWeight: 600,
    color: c.plum, lineHeight: 1.35,
    marginBottom: '20px',
    letterSpacing: '-0.015em',
  },
  textarea: {
    width: '100%',
    border: `1px solid ${c.line}`,
    borderRadius: '8px',
    padding: '12px 14px',
    fontSize: '15px', lineHeight: 1.5,
    fontFamily: 'inherit', color: c.ink,
    background: c.cream2 || '#faf8f5',
    outline: 'none', resize: 'vertical',
    minHeight: '140px',
  },
  savingDot: {
    fontSize: '11px', color: c.inkMuted, fontStyle: 'italic',
    marginTop: '8px', textAlign: 'right',
  },

  actionsRow: {
    display: 'flex', gap: '10px', alignItems: 'center',
    flexWrap: 'wrap', marginBottom: '14px',
  },
  primaryBtn: {
    background: c.green, color: c.white,
    border: 'none', borderRadius: '10px',
    padding: '12px 20px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    flex: 1, justifyContent: 'center',
  },
  secondaryBtn: {
    background: c.white, color: c.inkMuted,
    border: `1px solid ${c.line}`, borderRadius: '10px',
    padding: '12px 16px', fontSize: '13px', fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
  },
  skipBtn: {
    background: 'none', color: c.inkMuted,
    border: `1px dashed ${c.line}`, borderRadius: '10px',
    padding: '12px 16px', fontSize: '13px', fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
  },
  backBtn: {
    background: c.green, color: c.white,
    border: 'none', borderRadius: '10px',
    padding: '12px 20px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', display: 'block', margin: '0 auto',
  },

  bottomMeta: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '12px', color: c.inkMuted,
    padding: '8px 4px',
  },
  allDoneHint: { color: c.green, fontWeight: 500 },
}
